import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { STAFF_DEVICE_PEER_RAW } from '@scio3/graphql-client';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@scio3/ui';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';

interface DevicePeerRaw {
  _id: string;
  publicKey: string;
  name: string;
  ownerEmail?: string | null;
  vpnIp: string;
  subnet: string;
  region: string;
  createdByRole: string;
  createdByTokenId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface StaffDevicePeerResult {
  staffDevicePeer: DevicePeerRaw | null;
}

interface Props {
  open: boolean;
  device: MdmDevice | null;
  onClose: () => void;
}

/**
 * Raw JSON viewer for the staff MDM device drawer. Shows the full Device
 * GraphQL document (already in memory) and the corresponding wg-orchestrator
 * `mdm-devices` peer row.
 *
 * Stacking note: uses the standard `@scio3/ui` `Dialog` wrapper at body z-50.
 * The MDM device drawer (now `InShellPanel`) sits at local z-20 inside
 * `#page-wrapper`, so this dialog naturally stacks above it via DOM order —
 * no z-index workarounds.
 */
export function DeviceJsonDialog({ open, device, onClose }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'device' | 'peer'>('device');

  const { data, loading, error } = useQuery<StaffDevicePeerResult>(STAFF_DEVICE_PEER_RAW, {
    variables: { uuid: device?.UUID ?? '' },
    skip: !open || !device?.UUID,
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  // Apollo Client 4 exposes GraphQL errors on `error.errors`; the legacy
  // Apollo v3 shape used `error.graphQLErrors`. Check both to stay resilient.
  const errLike = error as
    | {
        errors?: Array<{ extensions?: Record<string, unknown> }>;
        graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>;
      }
    | undefined;
  const gqlErrors = errLike?.errors ?? errLike?.graphQLErrors ?? [];
  const isForbidden = gqlErrors.some((e) => e.extensions?.code === 'FORBIDDEN');
  const peerAvailable = !isForbidden;
  const peer = data?.staffDevicePeer ?? null;

  const deviceJson = device ? JSON.stringify(device, null, 2) : '';
  const peerJson = peer ? JSON.stringify(peer, null, 2) : '';

  const handleCopy = async () => {
    const text = tab === 'device' ? deviceJson : peerJson;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('Copied to clipboard'));
    } catch {
      toast.error(t('Copy failed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        data-testid="mdm-device-json-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('Raw device JSON')}</DialogTitle>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(next) => setTab(next as 'device' | 'peer')}
          className="mt-3 flex-1 flex flex-col overflow-hidden min-h-0"
        >
          <TabsList className="shrink-0 self-start">
            <TabsTrigger value="device" className="cursor-pointer">
              {t('Device')}
            </TabsTrigger>
            {peerAvailable && (
              <TabsTrigger value="peer" className="cursor-pointer">
                {t('Peer (mdm-devices)')}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="device" className="flex-1 overflow-y-auto mt-2 min-h-0">
            <pre className="rounded-md border bg-muted p-3 text-xs font-mono whitespace-pre-wrap break-all">
              {deviceJson || t('No device loaded')}
            </pre>
          </TabsContent>

          {peerAvailable && (
            <TabsContent value="peer" className="flex-1 overflow-y-auto mt-2 min-h-0">
              {loading ? (
                <div className="text-sm text-muted-foreground p-3">{t('Loading...')}</div>
              ) : peer ? (
                <pre className="rounded-md border bg-muted p-3 text-xs font-mono whitespace-pre-wrap break-all">
                  {peerJson}
                </pre>
              ) : (
                <div className="text-sm text-muted-foreground p-3">
                  {t('No peer registered for this device (SecureLink not activated).')}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <Button
            variant="outline"
            size="icon-md"
            onClick={handleCopy}
            aria-label={t('Copy JSON')}
            data-testid="mdm-device-json-copy"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
